import React, { useState } from 'react';
import GamifiedButton from '../common/GamifiedButton';
import { useLang } from '../../i18n/LanguageContext';

const ReviewForm = ({ onSubmit, isSubmitting }) => {
    const { t } = useLang();
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');
    const [hoverRating, setHoverRating] = useState(0);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({ rating, title, comment });
        // Reset form is handled by parent or we can do it here if successful
        setTitle('');
        setComment('');
        setRating(5);
    };

    return (
        <form className="bg-gradient-to-br from-white to-[#F7F4EC] p-10 md:p-8 sm:p-6 rounded-2xl border-2 border-[#4AA294] shadow-[0_12px_40px_rgba(212,165,116,0.2)] backdrop-blur-[10px]" onSubmit={handleSubmit}>
            <h3 className="text-[1.8rem] md:text-xl sm:text-2xl font-black text-[#33403B] m-0 mb-8 md:mb-6 sm:mb-5 text-center font-['Fredoka',_'Poppins',_sans-serif] bg-gradient-to-br from-[#33403B] to-[#2F7E72] bg-clip-text text-transparent">{t('avis.formulaire.titre')}</h3>

            <div className="mb-8 md:mb-6 sm:mb-5 flex flex-col gap-3">
                <label className="block font-bold text-[#33403B] text-base md:text-[0.95rem] tracking-wide font-['Fredoka',_'Poppins',_sans-serif]">{t('avis.formulaire.note')}</label>
                <div className="flex items-center gap-2 md:gap-1 sm:gap-1 p-4 md:p-3 sm:p-2 bg-gradient-to-br from-[#4AA294]/10 to-[#EDB82F]/5 rounded-xl border border-[#4AA294]/30 w-fit sm:flex-wrap">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <button
                            key={star}
                            type="button"
                            className={`bg-none border-none text-[2.5rem] md:text-[2rem] sm:text-[1.8rem] cursor-pointer transition-all duration-200 ease-[cubic-bezier(0.34,1.56,0.64,1)] p-0 leading-none flex items-center justify-center hover:scale-125 hover:rotate-[15deg] hover:[filter:drop-shadow(0_4px_8px_rgba(255,215,0,0.3))] ${star <= (hoverRating || rating) ? 'text-[#EDB82F] [text-shadow:0_0_8px_rgba(255,215,0,0.4)] scale-115' : 'text-[#E5E7EB]'}`}
                            onClick={() => setRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            aria-label={t('avis.formulaire.noterAria', { n: star })}
                        >
                            ★
                        </button>
                    ))}
                    <span className="ml-4 md:ml-3 sm:ml-0 sm:mt-2 sm:w-full sm:text-center text-base md:text-[0.9rem] sm:text-[0.85rem] text-[#2F7E72] font-semibold font-['Fredoka',_'Poppins',_sans-serif] whitespace-nowrap sm:whitespace-normal bg-gradient-to-br from-[#4AA294] to-[#EDB82F] bg-clip-text text-transparent">
                        {rating === 5 && t('avis.formulaire.note5')}
                        {rating === 4 && t('avis.formulaire.note4')}
                        {rating === 3 && t('avis.formulaire.note3')}
                        {rating === 2 && t('avis.formulaire.note2')}
                        {rating === 1 && t('avis.formulaire.note1')}
                    </span>
                </div>
            </div>

            <div className="mb-8 md:mb-6 sm:mb-5 flex flex-col gap-3">
                <label htmlFor="title" className="block font-bold text-[#33403B] text-base md:text-[0.95rem] tracking-wide font-['Fredoka',_'Poppins',_sans-serif]">{t('avis.formulaire.titreAvis')}</label>
                <input
                    type="text"
                    id="title"
                    className="w-full p-4 md:p-3 sm:p-3 border-2 border-[#4AA294] rounded-xl text-base font-['Fredoka',_'Poppins',_sans-serif] bg-white text-[#33403B] transition-all duration-300 shadow-[0_2px_8px_rgba(212,165,116,0.1)] placeholder:text-[#B0B0B0] focus:outline-none focus:border-[#EDB82F] focus:shadow-[0_0_0_4px_rgba(255,215,0,0.2),0_4px_16px_rgba(212,165,116,0.2)] focus:bg-white valid:border-[#10B981]"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('avis.formulaire.titrePlaceholder')}
                    required
                    maxLength={100}
                />
            </div>

            <div className="mb-8 md:mb-6 sm:mb-5 flex flex-col gap-3">
                <label htmlFor="comment" className="block font-bold text-[#33403B] text-base md:text-[0.95rem] tracking-wide font-['Fredoka',_'Poppins',_sans-serif]">{t('avis.formulaire.commentaire')}</label>
                <textarea
                    id="comment"
                    className="w-full p-4 md:p-3 sm:p-3 border-2 border-[#4AA294] rounded-xl text-base sm:text-base font-['Fredoka',_'Poppins',_sans-serif] bg-white text-[#33403B] transition-all duration-300 shadow-[0_2px_8px_rgba(212,165,116,0.1)] resize-vertical min-h-[120px] sm:min-h-[100px] leading-relaxed placeholder:text-[#B0B0B0] focus:outline-none focus:border-[#EDB82F] focus:shadow-[0_0_0_4px_rgba(255,215,0,0.2),0_4px_16px_rgba(212,165,116,0.2)] focus:bg-white valid:border-[#10B981]"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('avis.formulaire.commentairePlaceholder')}
                    required
                    rows={4}
                    maxLength={500}
                />
            </div>

            <GamifiedButton
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="!w-full !justify-center !p-4 md:!p-3 sm:!px-6 sm:!py-3 !text-[1.05rem] md:!text-base sm:!text-base !font-bold !tracking-wide mt-4 relative overflow-hidden"
            >
                {isSubmitting ? t('avis.formulaire.envoiEnCours') : t('avis.formulaire.publier')}
            </GamifiedButton>
        </form>
    );
};

export default ReviewForm;
